import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { CheckCircle2, XCircle, RefreshCw, AlertTriangle, ExternalLink } from "lucide-react";
import { EbayNewsletterWidget } from "./EbayNewsletterWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * EbayTab - Admin view for eBay marketplace integration.
 * - Setup status (credentials, policies, location)
 * - Config form (insert credentials into MongoDB for production)
 * - Test connection (force OAuth refresh)
 * - List business policies retrieved from eBay
 * - List merchant locations
 * - Articles sync status table with manual republish/end-listing buttons
 */

const EbayConfigForm = ({ onSaved }) => {
  const [cfg, setCfg] = React.useState(null);
  const [form, setForm] = React.useState({
    app_id: "", cert_id: "", refresh_token: "", ru_name: "",
    fulfillment_policy_id: "", payment_policy_id: "", return_policy_id: "",
    merchant_location_key: "", sync_enabled: false,
  });
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [open, setOpen] = React.useState(false);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("access_token") || ""}`,
  });

  // getAuthHeaders is recreated on each render but reads from sessionStorage
  // (always fresh), so empty deps are intentionally safe.
  const loadCurrent = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ebay/config`, { headers: getAuthHeaders() });
      setCfg(r.data);
      // Pre-fill: use saved DB values, OR pre-populate with Edicola Cerasuolo known defaults if empty
      setForm(f => ({
        ...f,
        ru_name: r.data.ru_name || "C.E.D._MC_DI_MA-CEDMCDIM-Edicol-hnbrqlt",
        fulfillment_policy_id: r.data.fulfillment_policy_id || "277194187018",
        payment_policy_id: r.data.payment_policy_id || "277194188018",
        return_policy_id: r.data.return_policy_id || "277279144018",
        merchant_location_key: r.data.merchant_location_key || "MAGAZZINO_VILLARICCA",
        sync_enabled: r.data.sync_enabled === true,
      }));
    } catch {
      setCfg({ error: "Errore caricamento" });
    }
  }, []);

  React.useEffect(() => { if (open) loadCurrent(); }, [open, loadCurrent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    // Validation: at least one of (app_id, cert_id, refresh_token) must be provided
    // if not already stored (cfg.app_id_masked === "")
    const allEmpty = !form.app_id.trim() && !form.cert_id.trim() && !form.refresh_token.trim();
    const noneStored = !cfg?.app_id_masked && !cfg?.cert_id_masked && !cfg?.refresh_token_masked;
    if (allEmpty && noneStored) {
      setMsg({ ok: false, text: "❌ Inserisci almeno App ID, Cert ID e Refresh Token (i 3 campi segreti in alto)." });
      setSaving(false);
      return;
    }
    try {
      // Only include non-empty fields (but always include sync_enabled boolean)
      const payload = Object.fromEntries(
        Object.entries(form).filter(([k, v]) => {
          if (k === "sync_enabled") return true;
          return v && (typeof v === "string" ? v.trim() : true);
        })
      );
      const r = await axios.post(`${API}/ebay/save-config`, payload, { headers: getAuthHeaders() });
      setMsg({ ok: true, text: `✅ Salvato. Campi: ${r.data.saved_fields?.join(", ") || "—"}` });
      if (onSaved) onSaved();
      await loadCurrent();
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-gray-800 border-gray-700" data-testid="ebay-config-form-card">
      <CardHeader>
        <CardTitle className="text-white flex items-center justify-between">
          <span>🔧 Configurazione Credenziali</span>
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid="ebay-config-toggle-btn">
            {open ? "Nascondi" : "Apri form"}
          </Button>
        </CardTitle>
      </CardHeader>
      {open && (
        <CardContent>
          {cfg && (
            <div className="mb-4 p-3 bg-gray-900 rounded text-xs text-gray-400 space-y-1">
              <div>App ID attuale: <code className="text-amber-300">{cfg.app_id_masked || "—"}</code></div>
              <div>Cert ID attuale: <code className="text-amber-300">{cfg.cert_id_masked || "—"}</code></div>
              <div>Refresh Token: <code className="text-amber-300">{cfg.refresh_token_masked || "—"}</code></div>
            </div>
          )}
          {msg && (
            <Alert className={`mb-4 ${msg.ok ? "border-green-700 bg-green-900/30" : "border-red-700 bg-red-900/30"}`}>
              <AlertDescription className={msg.ok ? "text-green-200" : "text-red-200"}>{msg.text}</AlertDescription>
            </Alert>
          )}
          <Alert className="mb-4 border-amber-700 bg-amber-900/30">
            <AlertDescription className="text-amber-200 text-xs">
              ℹ️ I primi 3 campi (App ID, Cert ID, Refresh Token) sono <b>segreti e obbligatori</b> → devi incollare i valori che ti ho dato in chat.
              Gli altri 5 sono <b>già pre-compilati</b> coi valori di Edicola Cerasuolo, lasciali così.
            </AlertDescription>
          </Alert>
          <form onSubmit={handleSubmit} className="space-y-3 text-sm" autoComplete="off" data-testid="ebay-cfg-form">
            {/* Hidden honeypot to prevent browser auto-fill */}
            <input type="text" name="email" autoComplete="username" style={{display: "none"}} />
            <input type="password" name="password" autoComplete="new-password" style={{display: "none"}} />
            {[
              ["app_id", "App ID (Client ID)", "CEDMCDIM-EdicolaV-PRD-..."],
              ["cert_id", "Cert ID (Client Secret)", "PRD-1b076cb5b401-..."],
              ["refresh_token", "Refresh Token (lasciare vuoto se già salvato)", "v^1.1#i^1#..."],
              ["ru_name", "RuName", "C.E.D._MC_DI_MA-CEDMCDIM-Edicol-..."],
              ["fulfillment_policy_id", "Fulfillment Policy ID", "277194187018"],
              ["payment_policy_id", "Payment Policy ID", "277194188018"],
              ["return_policy_id", "Return Policy ID", "277279144018"],
              ["merchant_location_key", "Merchant Location Key", "MAGAZZINO_VILLARICCA"],
            ].map(([key, label, placeholder]) => (
              <div key={key}>
                <label className="block text-xs text-gray-300 mb-1">{label}</label>
                <input
                  type="text"
                  name={`ebay_${key}_${Math.random().toString(36).slice(2)}`}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={placeholder}
                  className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-gray-100 text-sm font-mono"
                  data-testid={`ebay-cfg-${key}-input`}
                />
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-gray-700">
              <input
                type="checkbox"
                id="ebay_sync_enabled"
                checked={form.sync_enabled}
                onChange={(e) => setForm({ ...form, sync_enabled: e.target.checked })}
                className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                data-testid="ebay-cfg-sync-enabled-checkbox"
              />
              <label htmlFor="ebay_sync_enabled" className="text-sm text-gray-200 cursor-pointer">
                🟢 <b>Sync abilitata</b> — quando spuntato, i nuovi articoli si pubblicano automaticamente su eBay
              </label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="ebay-cfg-save-btn">
                {saving ? "Salvataggio..." : "💾 Salva config"}
              </Button>
              <Button type="button" variant="outline" onClick={loadCurrent} disabled={saving}>
                Ricarica
              </Button>
              <Button
                type="button"
                variant="outline"
                className="text-red-400 border-red-700 hover:bg-red-900/20"
                disabled={saving}
                onClick={async () => {
                  if (!window.confirm("Resettare tutti i valori della config eBay nel DB?")) return;
                  setSaving(true);
                  try {
                    await axios.post(`${API}/ebay/save-config`, {
                      app_id: " ", cert_id: " ", refresh_token: " ", ru_name: " ",
                      fulfillment_policy_id: " ", payment_policy_id: " ", return_policy_id: " ",
                      merchant_location_key: " ", sync_enabled: false,
                      _reset: true,
                    }, { headers: getAuthHeaders() });
                    setMsg({ ok: true, text: "✅ Config resettata. Ora compila i campi correttamente." });
                    setForm({
                      app_id: "", cert_id: "", refresh_token: "", ru_name: "",
                      fulfillment_policy_id: "", payment_policy_id: "", return_policy_id: "",
                      merchant_location_key: "", sync_enabled: false,
                    });
                    if (onSaved) onSaved();
                    await loadCurrent();
                  } catch (err) {
                    setMsg({ ok: false, text: err.response?.data?.detail || err.message });
                  } finally {
                    setSaving(false);
                  }
                }}
                data-testid="ebay-cfg-reset-btn"
              >
                🗑️ Reset
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              ℹ️ I valori vengono salvati nel MongoDB (collection <code>ebay_config</code>). Lascia un campo vuoto per <b>non modificare</b> il valore già salvato.
            </p>
          </form>
        </CardContent>
      )}
    </Card>
  );
};

export const EbayTab = () => {
  const [status, setStatus] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [policies, setPolicies] = useState(null);
  const [locations, setLocations] = useState(null);
  const [syncList, setSyncList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("access_token") || ""}`,
  });

  // Same pattern as loadCurrent: getAuthHeaders reads sessionStorage live.
  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ebay/status`, { headers: getAuthHeaders() });
      setStatus(r.data);
    } catch (e) {
      setStatus({ error: e.response?.data?.detail || e.message });
    }
  }, []);

  const fetchSyncList = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/ebay/articles-sync-status`, { headers: getAuthHeaders() });
      setSyncList(r.data.items || []);
    } catch {
      setSyncList([]);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchSyncList();
  }, [fetchStatus, fetchSyncList]);

  const handleTestConnection = async () => {
    setLoading(true);
    setTestResult(null);
    try {
      const r = await axios.get(`${API}/ebay/test-connection`, { headers: getAuthHeaders() });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleListPolicies = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ebay/policies`, { headers: getAuthHeaders() });
      setPolicies(r.data);
    } catch (e) {
      setPolicies({ error: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleListLocations = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ebay/locations`, { headers: getAuthHeaders() });
      setLocations(r.data);
    } catch (e) {
      setLocations({ error: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleManualPublish = async (articleId) => {
    setLoading(true);
    setActionMsg(null);
    try {
      const r = await axios.post(`${API}/ebay/publish/${articleId}`, {}, { headers: getAuthHeaders() });
      setActionMsg({ ok: true, text: `Pubblicato! Offer ID: ${r.data.offer_id || "—"} · Listing ID: ${r.data.listing_id || "—"}` });
      await fetchSyncList();
    } catch (e) {
      setActionMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleManualEnd = async (articleId) => {
    if (!window.confirm("Confermi di voler ritirare il listing eBay per questo articolo?")) return;
    setLoading(true);
    setActionMsg(null);
    try {
      await axios.post(`${API}/ebay/end/${articleId}`, {}, { headers: getAuthHeaders() });
      setActionMsg({ ok: true, text: "Listing eBay ritirato" });
      await fetchSyncList();
    } catch (e) {
      setActionMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncOrders = async () => {
    setLoading(true);
    setActionMsg(null);
    try {
      const r = await axios.post(
        `${API}/ebay/sync-orders?lookback_hours=24`,
        {},
        { headers: getAuthHeaders() }
      );
      const d = r.data || {};
      if (d.ok === false) {
        setActionMsg({ ok: false, text: `Sync ordini fallita: ${d.reason || "errore sconosciuto"}` });
      } else {
        const errs = d.stock_errors?.length || 0;
        setActionMsg({
          ok: true,
          text: `✅ Sync ordini eBay completata: ${d.fetched || 0} fetchati, ${d.new || 0} NUOVI, ${d.skipped || 0} già importati${errs ? `, ⚠️ ${errs} avvisi SKU` : ""}.`,
        });
      }
    } catch (e) {
      setActionMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const handleFixCombinedShipping = async () => {
    if (!window.confirm(
      "Imposterà la spedizione 'aggiuntiva' a €0 su TUTTE le tue Fulfillment Policy eBay.\n\n" +
      "Risultato: un cliente che compra 2+ articoli pagherà la spedizione SOLO sul primo. " +
      "Procedere?"
    )) return;
    setLoading(true);
    setActionMsg(null);
    try {
      const r = await axios.post(
        `${API}/ebay/fix-combined-shipping`,
        {},
        { headers: getAuthHeaders() }
      );
      const d = r.data || {};
      const updated = (d.updated_policies || []).filter(p => p.status === "updated").length;
      const already = (d.updated_policies || []).filter(p => p.status === "already_zero").length;
      const errors = (d.updated_policies || []).filter(p => p.status === "error").length;
      setActionMsg({
        ok: errors === 0,
        text: `✅ Spedizione combinata: ${updated} policy aggiornate, ${already} già a €0, ${d.shipping_services_fixed || 0} servizi fixati${errors ? `, ❌ ${errors} errori` : ""}. ${d.next_step || ""}`,
      });
    } catch (e) {
      setActionMsg({ ok: false, text: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  const renderStatusRow = (label, ok, value) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
      <span className="text-sm text-gray-300">{label}</span>
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 className="w-4 h-4 text-green-500" />
        ) : (
          <XCircle className="w-4 h-4 text-red-500" />
        )}
        <span className="text-xs text-gray-400">{value}</span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ebay-tab">
      <div>
        <h2 className="text-2xl font-bold text-white">🟦 Integrazione eBay</h2>
        <p className="text-gray-400 text-sm">Sync unidirezionale Sito → eBay Marketplace IT</p>
      </div>

      {actionMsg && (
        <Alert className={actionMsg.ok ? "border-green-700 bg-green-900/30" : "border-red-700 bg-red-900/30"}>
          <AlertDescription className={actionMsg.ok ? "text-green-200" : "text-red-200"}>
            {actionMsg.text}
          </AlertDescription>
        </Alert>
      )}

      <EbayNewsletterWidget />

      {/* Setup Status Card */}
      <Card className="bg-gray-800 border-gray-700" data-testid="ebay-status-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Stato Setup</span>
            <Button
              size="sm"
              variant="outline"
              onClick={fetchStatus}
              disabled={loading}
              data-testid="ebay-refresh-status-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Aggiorna
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!status ? (
            <p className="text-gray-400 text-sm">Caricamento...</p>
          ) : status.error ? (
            <Alert className="border-red-700 bg-red-900/30">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <AlertDescription className="text-red-200">{status.error}</AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-1">
              {renderStatusRow(
                "Credenziali OAuth (App ID + Cert ID + Refresh Token)",
                status.credentials_configured,
                status.credentials_configured ? "Configurate" : "Mancanti — usa il form sotto"
              )}
              {renderStatusRow(
                "Marketplace",
                status.marketplace === "EBAY_IT",
                status.marketplace
              )}
              {renderStatusRow(
                "Sync attiva",
                !status.sync_disabled,
                status.sync_disabled ? "DISABILITATA" : "Abilitata"
              )}
              {renderStatusRow(
                "Fulfillment Policy",
                status.policies?.fulfillment,
                status.policies?.fulfillment ? "Configurata" : "Manca — usa il form sotto"
              )}
              {renderStatusRow(
                "Payment Policy",
                status.policies?.payment,
                status.policies?.payment ? "Configurata" : "Manca — usa il form sotto"
              )}
              {renderStatusRow(
                "Return Policy",
                status.policies?.return,
                status.policies?.return ? "Configurata" : "Manca — usa il form sotto"
              )}
              {renderStatusRow(
                "Merchant Location Key",
                Boolean(status.merchant_location_key),
                status.merchant_location_key || "Manca — usa il form sotto"
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Config Form (per inserire credenziali direttamente nel DB di produzione) */}
      <EbayConfigForm onSaved={fetchStatus} />

      {/* Tools Card */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Strumenti di Setup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button onClick={handleTestConnection} disabled={loading} data-testid="ebay-test-connection-btn">
              🔐 Test OAuth Connection
            </Button>
            <Button onClick={handleListPolicies} disabled={loading} variant="outline" data-testid="ebay-list-policies-btn">
              📋 Lista Business Policies
            </Button>
            <Button onClick={handleListLocations} disabled={loading} variant="outline" data-testid="ebay-list-locations-btn">
              📍 Lista Merchant Locations
            </Button>
            <Button
              onClick={handleSyncOrders}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="ebay-sync-orders-btn"
            >
              🔄 Sincronizza Ordini eBay
            </Button>
            <Button
              onClick={handleFixCombinedShipping}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="ebay-fix-combined-shipping-btn"
            >
              📦 Fix Spedizione Combinata
            </Button>
          </div>

          {testResult && (
            <Alert className={testResult.ok ? "border-green-700 bg-green-900/30" : "border-red-700 bg-red-900/30"}>
              <AlertDescription className={testResult.ok ? "text-green-200" : "text-red-200"}>
                {testResult.ok ? "✅ " : "❌ "}{testResult.message}
              </AlertDescription>
            </Alert>
          )}

          {policies && (
            <details className="bg-gray-900 rounded p-3" open>
              <summary className="text-sm font-medium text-amber-400 cursor-pointer">
                Business Policies trovate
              </summary>
              <pre className="text-xs text-gray-300 mt-2 overflow-auto max-h-96">
                {JSON.stringify(policies, null, 2)}
              </pre>
              <p className="text-xs text-gray-400 mt-2">
                ℹ️ Copia i 3 ID (paymentPolicyId, fulfillmentPolicyId, returnPolicyId) nel file <code>.env</code> backend.
              </p>
            </details>
          )}

          {locations && (
            <details className="bg-gray-900 rounded p-3" open>
              <summary className="text-sm font-medium text-amber-400 cursor-pointer">
                Merchant Locations trovate
              </summary>
              <pre className="text-xs text-gray-300 mt-2 overflow-auto max-h-96">
                {JSON.stringify(locations, null, 2)}
              </pre>
              <p className="text-xs text-gray-400 mt-2">
                ℹ️ Copia il <code>merchantLocationKey</code> nel file <code>.env</code> come <code>EBAY_MERCHANT_LOCATION_KEY</code>.
              </p>
            </details>
          )}
        </CardContent>
      </Card>

      {/* Sync Status Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Articoli Sincronizzati ({syncList.length})</span>
            <Button size="sm" variant="outline" onClick={fetchSyncList} disabled={loading}>
              <RefreshCw className="w-3 h-3 mr-1" /> Aggiorna
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {syncList.length === 0 ? (
            <p className="text-gray-400 text-sm">Nessun articolo sincronizzato con eBay ancora.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="ebay-sync-table">
                <thead>
                  <tr className="border-b border-gray-700 text-gray-400">
                    <th className="text-left py-2 px-2">SKU</th>
                    <th className="text-left py-2 px-2">Titolo</th>
                    <th className="text-right py-2 px-2">€</th>
                    <th className="text-right py-2 px-2">Stock</th>
                    <th className="text-center py-2 px-2">Stato</th>
                    <th className="text-left py-2 px-2">Offer ID</th>
                    <th className="text-right py-2 px-2">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {syncList.map((it) => {
                    const ebay = it.ebay || {};
                    const statusVal = ebay.status || "—";
                    const statusColor =
                      statusVal === "active" ? "bg-green-700"
                      : statusVal === "ended" ? "bg-gray-600"
                      : statusVal === "error" ? "bg-red-700"
                      : "bg-amber-700";
                    return (
                      <tr key={it.id} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-2 px-2 text-amber-300 font-mono">{it.sku}</td>
                        <td className="py-2 px-2 text-gray-200 max-w-xs truncate">{it.title}</td>
                        <td className="py-2 px-2 text-right text-gray-300">{it.price?.toFixed(2)}</td>
                        <td className="py-2 px-2 text-right text-gray-300">{it.quantity_stock}</td>
                        <td className="py-2 px-2 text-center">
                          <Badge className={`${statusColor} text-white text-xs`}>{statusVal}</Badge>
                        </td>
                        <td className="py-2 px-2 text-gray-400 font-mono text-xs">{ebay.offer_id || "—"}</td>
                        <td className="py-2 px-2 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleManualPublish(it.id)}
                              disabled={loading}
                              data-testid={`ebay-publish-btn-${it.id}`}
                              title="Ripubblica / aggiorna"
                            >
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                            {ebay.listing_id && (
                              <a
                                href={`https://www.ebay.it/itm/${ebay.listing_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center h-8 px-2 border border-gray-600 rounded hover:bg-gray-700"
                                title="Apri listing su eBay"
                              >
                                <ExternalLink className="w-3 h-3 text-amber-400" />
                              </a>
                            )}
                            {ebay.offer_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-400 border-red-700"
                                onClick={() => handleManualEnd(it.id)}
                                disabled={loading}
                                data-testid={`ebay-end-btn-${it.id}`}
                                title="Ritira listing"
                              >
                                ✕
                              </Button>
                            )}
                          </div>
                          {ebay.last_error && (
                            <p className="text-xs text-red-400 mt-1 max-w-xs truncate" title={ebay.last_error}>
                              ⚠️ {ebay.last_error}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
